import { ref, watch, computed, getCurrentInstance } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
// import { updateImprovement } from '@/@core/queries/improvements'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import awsConnection from '@/views/habit/aws'
import { useLambda } from '@/views/habit/awsLambda'
import useComplaintEdit from '@/views/organization/complaint/edit/useComplaintEdit'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'
import { oid } from '@/helpers/mongoHelpers'
import { permissionScope, hasPermission } from '@/composables/usePermission'
import useCommonTodo from '@/views/apps/todo/useCommonTodo'

export default function useComplaintList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError, openDocument, downloadZip } = useCommon()
  const { uploadFileToS3 } = useCommonTodo()
  const { updateItem, updateItems } = useLambda()
  const { singleUpload } = awsConnection()
  const { complaintsQueryWithOffset } = useMongoDBFunctions()
  const { client, worker_id, username, email, role } = store.state?.userStore?.userData
  const { default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')
  const refComplaintTable = ref(null)
  const complaints = ref([])
  const complaintStats = ref({})
  const perPage = ref(10)
  const totalComplaints = ref(0)
  const currentPage = ref(1)
  const searchQuery = ref('')
  const sortBy = ref('createdAt')
  const isSortDirDesc = ref(true)
  const isLoading = ref(true)
  const workerFilter = ref([])
  const activeState = ref('total')
  const activeStage = ref(null)
  const pendingTasksFilter = ref(false)
  const typeFilter = ref(null)
  const statesData = ref({})
  const stagesData = ref({})
  const clearFiltersDisabled = computed(() => !searchQuery.value && activeState.value === 'total' && !activeStage.value && !pendingTasksFilter.value && !typeFilter.value && (!companyFilter.value || companyFilter.value.length === 0))
  const finishJustification = ref("")
  const evidenceFiles = ref([])

  // Get company filter from store
  const companyFilter = computed(() => store.state.filtersStore?.companyFilter || [])

  // Table Handlers
  const tableColumns = computed(() => {
    return [
      {
        key: 'Actions',
        label: i18n.t('message.tableHeader.actions'),
      },
      {
        key: 'type',
        label: i18n.t('label.Type'),
        sortable: true,
      },
      {
        key: 'company',
        label: i18n.t('label.company'),
        sortable: true,
      },
      {
        key: 'themes',
        label: i18n.t('label.themes'),
        sortable: true,
      },
      {
        key: 'createdAt',
        label: i18n.t('label.registration_date'),
        sortable: true,
      },
      {
        key: 'caseNumber',
        label: i18n.t('message.case_number'),
        sortable: true,
      },
      {
        key: 'stage',
        label: i18n.t('improvement_opp.stage'),
        sortable: true,
      },
      {
        key: 'dueDate',
        label: i18n.t('label.next_stage_date'),
        sortable: true,
      },
      {
        key: 'state',
        label: i18n.t('improvement_opp.state'),
        sortable: true,
      },
    ]
  })

  const fetchComplaints = async () => {
    isLoading.value = true

    try {
      const input = {
        client_id: client.$oid,
        // workers: workerFilter.value,
        search: searchQuery.value,
        sortBy: sortBy.value || '_id',
        sortOrder: isSortDirDesc.value ? -1 : 1,
        limit: perPage.value,
        offset: (currentPage.value * perPage.value) - perPage.value,
        state: activeState.value,
        stage: activeStage.value,
        onlyPendingTasks: pendingTasksFilter.value,
        type: typeFilter.value,
        companies: companyFilter.value,
        workerId: worker_id?.$oid,
        workerName: username,
        workerEmail: email,
        restrictToOwn: permissionScope('complaint-channel', 'view') === 'own',
      }

      const { complaintsPaginated, complaintsStats } = await complaintsQueryWithOffset(input)
    
      complaintsPaginated.forEach(e => {
        // Format next stage date
        const datePropertyName = e.stage === 'registration' || e.stage === 'analysis' ? 'dueDate' : e.stage === 'approval' ? 'approvalDueDate' : 'verificationDueDate'
        const date = e[datePropertyName] ? new Date(e[datePropertyName]) : undefined
        e.dueDate = date?.toLocaleDateString(`${default_language || 'en'}-US`) || ''

        // Format registration date (keep original createdAt for sorting)
        const createdAtDate = e.createdAt ? new Date(e.createdAt) : undefined
        e.createdAtFormatted = createdAtDate?.toLocaleDateString(`${default_language || 'en'}-US`) || ''

        // Set type
        e.typeComputed = computed(() => {
          if (!e.type) return ''
          return i18n.t(`label.${e.type}`)
        })

        // Set company
        e.companyComputed = computed(() => {
          if (e.companyName) return e.companyName
          return ''
        })

        // Set themes
        e.themesComputed = computed(() => e.themes?.map(theme => theme.isNewOption ? theme.value : i18n.t(`complaintThemes.${theme.value}`)).join(" / "))

        // Set state
        e.stateComputed = computed(() => i18n.t(e.state))

        // Set stage with logic: if stage is 'verification' and there's at least 1 action plan in 'corrective', show 'corrective'
        e.stageComputed = computed(() => {
          if (!e.stage) return ''

          // Apply display logic
          const displayStage = e.stage === 'verification' && e.actionPlanStages?.some(plan => plan.stage === 'corrective')
            ? 'corrective'
            : e.stage

          return i18n.t(`complaintStages.${displayStage}`)
        })
      })

      complaints.value = complaintsPaginated
      complaintStats.value = complaintsStats.stateCounts.statsForCards
      totalComplaints.value = complaintsStats.totalCount
      statesData.value = {
        labels: complaintsStats.stateCounts.labels,
        values: complaintsStats.stateCounts.values,
        total: complaintsStats.totalCount
      }
      stagesData.value = {
        labels: complaintsStats.stageCounts.labels,
        values: complaintsStats.stageCounts.values,
        total: complaintsStats.totalCount
      }
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.err_complaints_list') })
    } finally {
      isLoading.value = false
    }
  }

  const deleteComplaint = (id) => {
    // axios
    //   .post('/graphql', {
    //     query: updateImprovement,
    //     variables: { query: { _id: id }, data: { deleted: true } },
    //   })
    //   .then(({ data }) => {
    //     if (data.errors) throw new Error(data.errors[0].message)
    //     showSuccessMessage(i18n.t('message.improvement_remove'))
    //     fetchComplaints()
    //   })
    //   .catch(error => {
    //     console.log(error)
    //     showErrorMessage(i18n.t('message.improvement_remove_error'))
    //   })
  }

  const finishComplaint = async (complaintData, finishJustification, evidenceFiles) => {
    if (!hasPermission('complaint-channel', 'delete')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    isLoading.value = true
    complaints.value = []
    let evidenceKeys = []

    // Upload evidences to AWS
    if (evidenceFiles?.length) {
      const destinationFolder = `${client.$oid}/evidences`
  
      const uploadFilesPromises = evidenceFiles.map(file => {
        if (!file?.name) return null
        return new Promise((resolve, reject) => {
          uploadFileToS3(file, destinationFolder)
            .then((key) => resolve(key))
            .catch((err) => {
              console.log(err)
              resolve(null)
            })
        })
      }) || []
  
      const fileKeys = await Promise.all(uploadFilesPromises)
      evidenceKeys = fileKeys.filter(key => !!key)
    }

    const query = { _id: oid(complaintData._id) }
    const payload = {
      stage: 'end',
      finishData: {
        justification: finishJustification,
        evidenceKeys,
      }
    }
    const action = { $set: payload, $push: { allFileKeys: { $each: evidenceKeys || [] } } }

    try {
      await updateItem({ collection: 'complaint', query, action })

      showSuccessMessage(i18n.t('message.complaint_updated'))

      // Finish all PACs
      if (complaintData.actionPlans?.length) {
        const query = { _id: { $in: complaintData.actionPlans.map(e => oid(e)) } }
        const payload = { stage: 'end' }
        const action = { $set: payload }
        updateItems({ collection: 'action_plan', query, action })
      }
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.complaint_update_error'))
      isLoading.value = false
    } finally {
      fetchComplaints()
      resetFinishModal()
    }
  }

  const resetFinishModal = () => {
    finishJustification.value = ""
    evidenceFiles.value = []
  }

  const handleAddEvidences = (newDocs) => {
    evidenceFiles.value.push(...newDocs)
  }

  const handleDeleteEvidences = (index) => {
    evidenceFiles.value.splice(index, 1)
  }

  const updateFilter = (data) => {
    workerFilter.value = data.workerFilter

    checkPageAndFetch()
  }

  const filterByStage = (stage) => {
    activeStage.value = activeStage.value === stage ? null : stage
    
    checkPageAndFetch()
  }

  const filterByState = (state, origin) => {
    if (activeState.value === state) {
      if (origin === 'fromChart') state = 'total'
      else return
    }
    activeState.value = state

    checkPageAndFetch()
  }

  const clearFilters = () => {
    const hasActiveFilters = searchQuery.value || pendingTasksFilter.value || typeFilter.value

    searchQuery.value = ''
    activeState.value = 'total'
    activeStage.value = null
    pendingTasksFilter.value = false
    typeFilter.value = null

    if (!hasActiveFilters) return    // This is to avoid duplicate data fetches

    checkPageAndFetch()
  }

  const checkPageAndFetch = () => {
    if (currentPage.value === 1) fetchComplaints()
    else currentPage.value = 1
  }

  watch([currentPage], () => {
    fetchComplaints()
  })

  watch([searchQuery, sortBy, isSortDirDesc, perPage, pendingTasksFilter, typeFilter, companyFilter], () => {
    checkPageAndFetch()
  })

  // Download to PDF
  const { proxy } = getCurrentInstance()
  const { show, complaintData } = useComplaintEdit()
  const isDownloading = ref(false)

  const downloadPDF = async (complaintId) => {
    if (!hasPermission('complaint-channel', 'export')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    try {
      isDownloading.value = true
      await show(complaintId)
      const data = complaintData.value

      // Eliminar cualquier contenedor de impresión anterior
      const oldContainer = document.getElementById('print-container')
      if (oldContainer) oldContainer.remove()

      // Crear contenedor oculto
      const container = document.createElement('div')
      container.id = 'print-container'
      container.style.position = 'fixed'
      container.style.top = '-9999px'
      container.style.left = '-9999px'
      document.body.appendChild(container)

      // Crear y montar el componente imprimible
      const FormConstructor = proxy.$root.$options.components.ComplaintPrintableForm
      const vm = new proxy.$root.constructor({
        parent: proxy.$root,
        render: (h) => h(FormConstructor, { props: { data } }),
      }).$mount()

      container.appendChild(vm.$el)

      // Esperar un frame para asegurar render completo
      await new Promise(resolve => setTimeout(resolve, 150))

      // Cambiar posición para impresión
      container.style.top = '0'
      container.style.left = '0'

      // Función de limpieza
      const cleanup = () => {
        vm.$destroy()
        if (container.parentNode) container.parentNode.removeChild(container)
        window.removeEventListener('afterprint', cleanup)
        isDownloading.value = false
      }

      // Escuchar cuando termina la impresión (incluso si se cancela)
      window.addEventListener('afterprint', cleanup, { once: true })

      // Abrir cuadro de impresión
      window.print()

      // Descargar todos los documentos y evidencias de la denuncia
      if (data.allFileKeys?.length) {
        await downloadZip(data.allFileKeys)
      }
    } catch (error) {
      console.error('Error al imprimir:', error)
      isDownloading.value = false
    }
  }

  return {
    fetchComplaints,
    tableColumns,
    perPage,
    currentPage,
    totalComplaints,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refComplaintTable,
    complaints,
    deleteComplaint,
    workerFilter,
    isLoading,
    complaintStats,
    updateFilter,
    filterByState,
    filterByStage,
    activeState,
    pendingTasksFilter,
    typeFilter,
    statesData,
    stagesData,
    clearFilters,
    clearFiltersDisabled,
    finishComplaint,
    finishJustification,
    resetFinishModal,
    evidenceFiles,
    openDocument,
    role,
    downloadPDF,
    isDownloading,
    handleAddEvidences,
    handleDeleteEvidences,
  }
}
